import type {
  DeliveryInfo,
  IncludedDiscounts,
  TaxedPrice
} from '@/modules/cart/domain/Cart';
import type { DiscountedLineItem } from '@/modules/cart/domain/CartLineItem';
import type { HttpCartProductOptionDto } from '@/modules/cart/infrastructure/dto/HttpCartProductOptionDto';
import type {
  Custom,
  LineItem,
  ShippingAddress
} from '@/modules/cart/infrastructure/dto/HttpCartRepositoryDto';
import type {
  EPriceValueType,
  PriceValueDto
} from '@/modules/shared/types/PriceDto';
import type { ECollectNodeAdmin } from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';

import type { EPayStatus } from './order.interface';

export interface ICustomAttributes {
  type: {
    id: string;
  };
  fields: Record<string, unknown>;
}

export interface IOrder {
  id: string;
  orderNumber: string;
  customerId: string;
  customerEmail: string;
  anonymousId: string;
  lineItems: LineItem[];
  productOptions: HttpCartProductOptionDto[];
  deliveryInfo: DeliveryInfo;
  totalPrice: PriceValueDto;
  taxedPrice: TaxedPrice;
  taxedShippingPrice: {
    totalNet: {
      centAmount: number;
      currencyCode: string;
      fractionDigits: number;
    };
    totalGross: {
      centAmount: number;
      currencyCode: string;
      fractionDigits: number;
    };
    taxPortions: [
      {
        name: string;
        rate: number;
        amount: {
          centAmount: number;
          currencyCode: string;
          fractionDigits: number;
        };
      }
    ];
    totalTax: {
      centAmount: number;
      currencyCode: string;
      fractionDigits: number;
    };
  };
  discountOnTotalPrice: {
    discountedAmount: {
      type: 'centPrecision';
      centAmount: number;
      currencyCode: string;
      fractionDigits: number;
    };
    includedDiscounts: [
      {
        discount: {
          id: string;
        };
        discountedAmount: {
          type: 'centPrecision';
          centAmount: number;
          currencyCode: string;
          fractionDigits: number;
        };
      }
    ];
    discountedNetAmount: {
      type: 'centPrecision';
      centAmount: number;
      currencyCode: string;
      fractionDigits: number;
    };
    discountedGrossAmount: {
      type: 'centPrecision';
      centAmount: number;
      currencyCode: string;
      fractionDigits: number;
    };
  };
  billingAddress: {
    id: string;
    key: string;
    title: string;
    salutation: string;
    firstName: string;
    lastName: string;
    streetName: string;
    streetNumber: string;
    additionalStreetInfo: string;
    postalCode: string;
    city: string;
    region: string;
    state: string;
    company: string;
    department: string;
    building: string;
    apartment: string;
    pOBox: string;
    phone: string;
    mobile: string;
    email: string;
    fax: string;
    externalId: string;
    country: string;
    additionalAddressInfo: string;
    custom: Custom;
  };
  shippingAddress: ShippingAddress;
  discountCodes: [
    {
      discountCode: {
        id: string;
      };
      state: string;
    }
  ];
  directDiscounts: [];
  payments: IPayment[];
  orderState: string;
  paymentState: string;
  state: {
    id: string;
  };
  customerTags: [];
  source: string;
  weddingId: string;
  weddingMessage: string;
  clubParisCode: string;
  clubParisCodeType: string;
  weddingEmail1: string;
  weddingEmail2: string;
  weddingFirstName1: string;
  weddingFirstName2: string;
  weddingItsGift: true;
  outOfStockItems: [];
  totalLineItemQuantity: number;
  tokenUserId?: string;
  customCartType?: string;
}

export interface ILineItem {
  id: string;
  productId: string;
  productKey: string;
  name: ILocalizedString;
  productSlug: ILocalizedString;
  productType: IProductTypeReference;
  variant: IProductVariant;
  price: IPrice;
  quantity: number;
  totalPrice: ICentPrecisionMoney;
  discountedPricePerQuantity: DiscountedLineItem[];
  taxedPrice: ITaxedItemPrice;
  distributionChannel: Channel;
  custom: ICustomFields;
  addedAt?: string;
  lastModifiedAt?: string;
}

interface IPrice {
  id: string;
  key: string;
  value: ITypedMoney;
  country?: string;
  channel: Channel;
  validFrom?: string;
  validUntil?: string;
  discounted?: unknown;
  custom?: ICustomFields;
}

interface ITypedMoney {
  type: EPriceValueType;
  centAmount: number;
  currencyCode: 'CLP';
  fractionDigits: number;
}

export interface Channel {
  typeId: string;
  id: string;
  obj?: Obj;
}

export interface Obj {
  id: string;
  version: number;
  versionModifiedAt: string;
  createdAt: string;
  lastModifiedAt: string;
  key: string;
  roles: string[];
  name: ILocalizedString;
  description: ILocalizedString;
}

export interface ICustomFields {
  type: ITypeReference;
  fields: IFieldContainer;
}

interface ITypeReference {
  id: string;
}

interface IFieldContainer {
  [key: string]: unknown;
}

interface IProductTypeReference {
  id: string;
}

export interface IProductVariant {
  id: number;
  key?: string;
  sku: string;
  prices: IPrices;
  brand: string;
  isMarketplace: boolean;
  isPreSale: boolean;
  departmentId?: string;
  subdepartmentId?: string;
  departmentJDA?: string;
  classId?: string;
  familyId?: string;
  sellers?: string[];
  sellersSKU?: string;
  upc?: string;
  ean?: string;
  collections?: string[];
  productTypePIM?: string;
  attributes?: IAttribute[];
  images: IImage[];
  assets?: IAsset[];
  availability?: IProductVariantAvailability;
  isMatchingVariant?: boolean;
}

interface IAttribute {
  name: string;
  value: string;
}

interface IImage {
  url: string;
  label?: string;
  dimensions: {
    w: number;
    h: number;
  };
}

interface IDimensions {
  w: number;
  h: number;
}

interface IAsset {
  id: string;
  key?: string;
  sources: IassetSource[];
  name: ILocalizedString;
  description?: ILocalizedString;
  tags?: string[];
  custom?: ICustomFields;
}

interface IassetSource {
  key?: string;
  uri: string;
  dimensions?: IDimensions;
  contentType?: string;
}

interface IProductVariantChannelAvailability {
  isOnStock?: boolean;
  restockableInDays?: number;
  availableQuantity?: number;
  version: number;
  id: string;
}

interface IProductVariantAvailabilityType {
  [x: string]: IProductVariantChannelAvailability;
}

interface IProductVariantAvailability {
  channels?: IProductVariantAvailabilityType;
  isOnStock?: boolean;
  restockableInDays?: number;
  availableQuantity?: number;
}

export interface IPrices {
  regular: IRegularPrice;
  offer?: IOfferPrice;
  paymentMethod?: IPaymentMethodPrice;
}

interface IRegularPrice {
  id: string;
  key?: string;
  value: ITypedMoney;
  discounted?: IDiscountedPrice;
}

interface IDiscountedPrice {
  value: ITypedMoney;
  discount: IProductDiscountReference;
}

interface IProductDiscountReference {
  id: string;
}

interface IOfferPrice {
  id: string;
  key?: string;
  value: ITypedMoney;
  discounted?: IDiscountedPrice;
  custom?: {
    type: {
      id: string;
    };
    fields: object;
  };
  discountOnRegular: number;
}

export enum EPricePaymentMethod {
  CENCOSUD_CARD = 'cencosudCard',
  CENCO_PAY = 'cencoPay'
}

interface IPaymentMethodPrice extends IOfferPrice, IRegularPrice {
  method: EPricePaymentMethod;
}

interface ITaxedItemPrice {
  totalNet: ICentPrecisionMoney;
  totalGross: ICentPrecisionMoney;
  totalTax: ICentPrecisionMoney;
}

export interface IDeliveryGroup {
  id: string;
  products?: string[];
  shippingOption?: IGeneralDeliveryShippingOptionResponse &
    IGeneralCollectShippingOptionResponse;
  optionSelected?: IGeneralDeliveryOptionResponse & IOption;
  collectOnBehalfName?: string;
  collectOnBehalfRut?: string;
  totalPrice: ICentPrecisionMoney;
  taxedPrice: PriceValueDto;
  proratedCost: ICentPrecisionMoney;
  includedDiscounts: IncludedDiscounts[];
}

interface IGeneralDeliveryOptionResponse {
  deliveryDateFrom?: string;
  deliveryDateTo?: string;
  shipDate?: string;
  timeSlots?: ITimeSlot[];
}

interface IOption {
  originNodeType?: NODE_TYPE | string;
  originNodeCode?: string;
  originNodeAdmin?: string;
  shippingCapability?: SHIPPING_CAPABILITY | string;
  collectNodeCode?: string;
  collectNodeType?: string;
  collectNodeAdmin?: ECollectNodeAdmin | string | undefined;
  collectNodeName?: string;
  collectNodeAddress?: ICollectNodeAddressResponse;
  rank?: number;
  deliveryDateFrom: string;
  deliveryDateTo: string;
  shipDate: string;
  timeSlots?: ITimeSlot[];
}

export type SHIPPING_TYPE = 'COLLECT' | 'DELIVERY';
export type SHIPPING_SUBTYPE = 'HOME' | 'IN_STORE' | 'COPEC';
export type SHIPPING_METHOD =
  | 'SCHEDULED'
  | 'NEXT_DAY'
  | 'SAME_DAY'
  | 'DATE_RANGE'
  | 'SCHEDULED_DEFERRED';

export type SHIPPING_CAPABILITY =
  | 'CLICK_AND_COLLECT'
  | 'BOPIS'
  | 'SHIP_FROM_STORE'
  | 'HOME_DELIVERY'
  | 'PUNTO_RETIRO'
  | 'CROSSDOCK';

export type NODE_TYPE = 'WAREHOUSE' | 'STORE' | 'SELLER' | 'PROVIDER';

export interface IGeneralDeliveryShippingOptionResponse {
  selected: boolean;
  shippingType?: SHIPPING_TYPE;
  shippingSubType?: SHIPPING_SUBTYPE;
  shippingMethod?: SHIPPING_METHOD;
  classification?: string;
  logisticsModel: string;
  shipmentCostTotal?: string;
  shipmentCostBase?: string;
  shipmentCostCurrency?: string;
  shippingCapability?: SHIPPING_CAPABILITY | string;
  originNodeType?: NODE_TYPE;
  originNodeCode?: string;
  originNodeAdmin?: string;
  options?: IGeneralDeliveryOptionResponse[];
}

interface IGeneralDeliveryOptionResponse {
  deliveryDateFrom?: string;
  deliveryDateTo?: string;
  shipDate?: string;
  timeSlots?: ITimeSlot[];
}

export type TIME_SLOT = 'AM' | 'TH' | 'PM';

export interface ITimeSlot {
  name: string | TIME_SLOT;
  deliveryTimeStart: string;
  deliveryTimeEnd: string;
}

export interface IGeneralCollectShippingOptionResponse {
  selected: boolean;
  shippingType?: SHIPPING_TYPE;
  shippingSubType?: SHIPPING_SUBTYPE;
  shippingMethod?: SHIPPING_METHOD;
  classification?: string;
  logisticsModel: string;
  shipmentCostTotal?: string;
  shipmentCostBase?: string | number;
  shipmentCostCurrency?: string;
  options?: IGeneralCollectOptionResponse[];
}

interface IGeneralCollectOptionResponse {
  deliveryDateFrom?: string;
  deliveryDateTo?: string;
  shipDate?: string;
  originNodeType?: NODE_TYPE | string;
  originNodeCode?: string;
  originNodeAdmin?: string;
  shippingCapability?: SHIPPING_CAPABILITY | string;
  collectNodeType?: NODE_TYPE | string;
  collectNodeCode?: string;
  collectNodeAdmin?: string;
  collectNodeName?: string;
  collectNodeAddress?: ICollectNodeAddressResponse;
  rank?: number;
  timeSlots?: ITimeSlot[];
}

interface ICollectNodeAddressResponse {
  collectNodeAddress1?: string;
  collectNodeAddress2?: string;
  collectRegionCode?: string;
  collectRegionName?: string;
  collectComunaCode?: string;
  collectComunaName?: string;
  uniqueReferences?: ICollectUniqueReferencesResponse;
}

interface ICollectUniqueReferencesResponse {
  collectComunaEOM?: string;
}

interface ITypedMoney {
  type: EPriceValueType;
  centAmount: number;
  currencyCode: 'CLP';
  fractionDigits: number;
}

export interface IPayment {
  id: string;
  amountPlanned: ICentPrecisionMoney;
  paymentMethodInfo: IPaymentMethodInfo;
  paymentStatus: IPaymentStatus;
  transactions: ITransaction[];
  interfaceInteractions: IInterfaceInteractions[];
}

interface ICentPrecisionMoney {
  centAmount: number;
  currencyCode: 'CLP';
  fractionDigits?: number;
}

interface IPaymentMethodInfo {
  paymentInterface: string;
  method: string;
  name: ILocalizedString;
}

export interface ILocalizedString {
  'es-CL': string;
}

interface IPaymentStatus {
  interfaceCode: string;
  interfaceText: string;
}

interface ITransaction {
  id: string;
  timestamp: string;
  type: TransactionType;
  amount: ICentPrecisionMoney;
  interactionId: string;
  state: TransactionState;
}

export enum TransactionType {
  Authorization = 'Authorization',
  CancelAuthorization = 'CancelAuthorization',
  Charge = 'Charge',
  Chargeback = 'Chargeback',
  Refund = 'Refund'
}

export enum TransactionState {
  Failure = 'Failure',
  Initial = 'Initial',
  Pending = 'Pending',
  Success = 'Success'
}

interface IGiftCardValidatedResponse {
  token: string;
  isNovios: boolean;
  giftCardType: string;
  balance: number;
  details: IGiftCardDetailDto[];
}

interface IGiftCardDetailDto {
  amount: number;
  depto: string;
}

export interface IOrderIntentStateResponse {
  state: EPayStatus;
  orderId: string;
  orderNumber: string;
}

export type GiftCardDetailResponse = {
  depto: string;
  monto: string;
};

export type PaymentIntentGiftCardResponse = {
  tarjeta: string;
  codigoAutorizacion: string;
  tipoGiftCard: string;
  montoDescuento: string;
  codresp: string;
  respuesta: string;
  detalle: GiftCardDetailResponse[];
};

export type TcomDetailResponse = {
  amount: number;
  status: string;
  authorizationCode: string;
  paymentTypeCode: string;
  responseCode: string;
  installmentsAmount: number;
  installmentsNumber: number;
  commerceCode: string;
  buyOrder: string;
  idQueryInstallments: string;
  transactionId: string;
  balance: number;
  responseMessage: string;
};

export type TcomCardDetail = {
  lastDigits: string;
};

export type PaymentIntentTcomResponse = {
  details: TcomDetailResponse[];
  buyOrder: string;
  sessionId: string;
  cardDetail: TcomCardDetail;
  accountingDate: string;
  transactionDate: string;
  version: string;
  token: string;
  traceToken: string;
  status: 'AUTHORIZED';
};

export type PaymentIntentWebpayError = {
  data: {
    error: {
      error_message: string;
    };
    paymentMethodId: string;
  };
  errorCode: string;
  name: string;
};

export type PaymentIntentWebpayResponse = {
  token: string;
  url: string;
  redirectTo: string;
};

export type PaymentIntentCencopayResponse = {
  tokenUUID: string;
  simpleCode: string;
  transactionId: string;
  timestamp: string;
  transactionCountdown: string;
  qrCode: string;
  baseDeeplink: string;
};

export type PaymentIntentCombinedResponse = Array<
  PaymentIntentGiftCardResponse | PaymentIntentTcomResponse
>;

export type PaymentIntentResponse =
  | PaymentIntentGiftCardResponse
  | PaymentIntentTcomResponse
  | PaymentIntentWebpayResponse
  | PaymentIntentWebpayError
  | PaymentIntentCencopayResponse
  | PaymentIntentCombinedResponse;

export type WebpayPaymentCardDetail = {
  card_number: string;
};

export type WebpayPaymentResponseDetail = {
  amount: number;
  status: string;
  authorization_code: string;
  payment_type_code: string;
  response_code: number;
  installments_number: number;
  commerce_code: string;
  buy_order: string;
};

export type WebpayPaymentResponse = {
  methodId: string;
  paymentMethodId: string;
  paymentProcessorId: string;
  vci: string;
  details: WebpayPaymentResponseDetail[];
  buy_order: string;
  session_id: string;
  accounting_date: string;
  transaction_date: string;
  card_detail: WebpayPaymentCardDetail;
};

export type IProcessorResponse = {
  cardDetail: {
    cardNumber: string;
  };
  card_detail?: {
    card_number?: string;
  };
  methodId: string;
  cardTypeName: string;
  paymentMethodName: string;
  paymentMethodId: string;
  cardType: string;
  transactionId: string;
  status: string;
  codresp: string;
  authorizationInfo: IAutorizationInfo[];
  detalle: GiftCardDetailResponse;
  pinpadResponse?: IPinpadReponse;
} & PaymentIntentResponse &
  PaymentIntentGiftCardResponse;

interface IAutorizationInfo {
  buyOrder: string;
  commerceCode: string;
  authorizationCode: string;
  paymentId: string;
  paymentMethod: string;
  lastDigits: string;
  amount: number;
  installments: number;
  paymentType: string;
  cardType: string;
  type: string;
}
interface IPinpadReponse {
  abrevTarjeta: string;
  bin: string;
  caja: string;
  cardType: string;
  cardTypeName: string;
  codAutorizacion: string;
  cuatroDigitos: string;
  fechaContable: string;
  fechaTransaccion: string;
  horaTransaccion: string;
  local: string;
  mensaje: string;
  nombreTarjetaHabiente: string;
  numCuotas: string;
  numeroUnico: string;
  rc: string;
  retailerId: string;
  terminalId: string;
  tipoCuotas: string;
  transaccionPremiada: string;
}
export interface IInterfaceInteractions {
  interactionId: string;
  token?: string;
  giftCardDetails?: IGiftCardValidatedResponse;
  processorResponse?: IProcessorResponse;
  tentativeOrderNumber?: string;
}
